import React, { PropsWithChildren, useContext, useCallback, useMemo, useEffect, useState, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';
import { generateFetchAdQuery } from '@moment/net/ad-fetch-query.ts';
import { IAdTagResponce } from '@pages/Root/services/api/ads/ad-model-definitions.ts';
import { GamAdsInitLayout } from './GamAdsInitLayout.tsx';
import { DTXAdsLayout } from './DTXAdsLayout.tsx';
import { AdsContext } from '@moment/context/AdsContext.tsx';
import { AdCounterContext } from '@moment/context/AdCounterContext.tsx';
import { CommonPageDataContext } from '@pages/Root/ContextWrapper/WithInterest/CommonPageDataContext.ts';
import { initializeAds, loadAdScripts, loadAllAdScripts } from '@pages/Root/utils/dtx-ads.ts';
import { tearDownPubwise } from '@moment/srv/utils/ad-utils.ts';
import { isDntEnabled } from '@utils/dnt.ts';
import { useAdStackAnalytics } from '@moment/analytics/AnalyticsProvider.tsx';
import {
  logAdImpressionMetric,
  logAdNotRenderedMetric,
  logAdRenderedMetric,
  logAdRequestedMetric,
} from '@/tracing/events.ts';

interface AdsLayoutProps extends PropsWithChildren {
  brandGroupIdOverride?: string;
  utmSourceOverride?: string;
}

export const AdsLayout: React.FC<AdsLayoutProps> = ({ brandGroupIdOverride, utmSourceOverride, children }) => {
  const [dtxScriptLoaded, setDTXScriptLoaded] = useState(false)
  const [pubwiseLoaded, setPubwiseLoaded] = useState(false)
  const [dtxInit, setDTXInit] = useState(false)

  const commonContext = useContext(CommonPageDataContext);
  const brandGroupId = brandGroupIdOverride || commonContext.brandGroupId;
  const utmSource = utmSourceOverride || commonContext.utmSource;
  const { appBundle, appName, appVersion, partner } = commonContext;
  const [searchParams] = useSearchParams();
  const adStackAnalytics = useAdStackAnalytics();
  const dtxInitStartedRef = useRef(false);

  const getMpid = useCallback((): string | undefined => {
    try {
      return searchParams.get('mpid') || searchParams.get('uid') || localStorage.getItem('mpid') || undefined;
    } catch (e) {
      console.log('Error reading mpid', e);
      return undefined;
    }
  }, [searchParams]);

  // Ad counter functionality for generating unique ad IDs
  const adCounter = useRef(0);
  const getAndIncrement = useCallback(() => {
    adCounter.current += 1;
    return adCounter.current;
  }, []);

  const adCounterContextValue = useMemo(
    () => ({ getAndIncrement }),
    [getAndIncrement]
  );

  const adQueryFn = useCallback(() => {
    const fetcher = generateFetchAdQuery()(brandGroupId, utmSource);
    return fetcher();
  }, [brandGroupId, utmSource]);

  const { data: adConfigData, isLoading } = useQuery<IAdTagResponce>({
    queryKey: ['adConfig', brandGroupId, utmSource],
    queryFn: adQueryFn,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    enabled: !!brandGroupId,
  });

  // The adSource is determined by the runtime configuration, which is provided by CommonPageDataContext.
  const adSource = useMemo(() => commonContext.adSource || 'none', [commonContext.adSource]);
  const hasGam = useMemo(() => adSource.includes('gam'), [adSource]);
  const hasDtx = useMemo(() => adSource.includes('dtx'), [adSource]);

  const handleAdImpression = useCallback((data: Record<string, object | string>) => {
    console.log(`DTX ad event 'impression' received: ${JSON.stringify(data)}`);
    const slotId: string = data.targetElementId as string;
    const adTagId: string = data.placementId as string;
    adStackAnalytics.emitViewableImpressionEvent({
      partnerId: partner || '',
      adTag: adTagId,
      adUnitId: slotId,
      searchParams,
    });
    logAdImpressionMetric({
      ad_unit_id: slotId,
      ad_tag: adTagId,
      utm_source: utmSource,
    });
  }, [adStackAnalytics, partner, searchParams, utmSource]);

  const handleAdRequested = useCallback((data: Record<string, object | string>) => {
    console.log(`DTX ad event 'ad_requested' received: ${JSON.stringify(data)}`);
    const slotId: string = data.targetElementId as string;
    const adTagId: string = data.placementId as string;
    logAdRequestedMetric({
      ad_unit_id: slotId,
      ad_tag: adTagId || '',
    });
  }, []);

  const handleAdRendered = useCallback((data: Record<string, object | string>) => {
    console.log(`DTX ad event 'ad_loaded' received: ${JSON.stringify(data)}`);
    const slotId: string = data.targetElementId as string;
    const adTagId: string = data.placementId as string;
    adStackAnalytics.emitImpressionEvent({
      partnerId: partner || '',
      adTag: adTagId,
      adUnitId: slotId,
      searchParams,
    });
    logAdRenderedMetric({
      ad_unit_id: slotId,
      ad_tag: adTagId || '',
    });
  }, [adStackAnalytics, partner, searchParams]);

  const handleAdNotRendered = useCallback((data: Record<string, object | string>) => {
    console.log(`DTX ad event 'ad_load_failed' received: ${JSON.stringify(data)}`);
    const slotId: string = data.targetElementId as string;
    const adTagId: string = data.placementId as string;
    adStackAnalytics.emitAdNotRenderedEvent({
      partnerId: partner || '',
      adTag: adTagId,
      adUnitId: slotId,
      searchParams,
    });
    logAdNotRenderedMetric({
      ad_unit_id: slotId,
      ad_tag: adTagId || '',
    });
  }, [adStackAnalytics, partner, searchParams]);

  useEffect(() => {
    if (hasGam && !isDntEnabled()) {
      loadAllAdScripts(['//securepubads.g.doubleclick.net/tag/js/gpt.js']);
    }
  }, [hasGam]);

  useEffect(() => {
    if ((hasGam || hasDtx) && !isDntEnabled()) {
      loadAllAdScripts(['//tagan.adlightning.com/mobileposse/op.js']);
    }
  }, [hasGam, hasDtx]);

  useEffect(() => {
    // Load and initialize the DTX SDK unconditionally (not gated on hasDtx) so that
    // prebid — which can invoke DTX bidding via window.DTXWebPrebidSpec regardless of
    // adSource — always has an initialized DTX SDK available in advance.
    if (dtxScriptLoaded || dtxInitStartedRef.current) {
      return;
    }
    dtxInitStartedRef.current = true;

    if (isDntEnabled()) {
      setDTXScriptLoaded(true);
      setDTXInit(true);
      return;
    }

    loadAdScripts().then(() => {
      setDTXScriptLoaded(true);
      const aaid = searchParams.get('aaid');
      const mpid = getMpid();
      initializeAds(
        [
          { eventName: 'impression', callback: handleAdImpression },
          { eventName: 'ad_requested', callback: handleAdRequested },
          { eventName: 'ad_loaded', callback: handleAdRendered },
          { eventName: 'ad_load_failed', callback: handleAdNotRendered },
        ],
        appBundle,
        appName,
        appVersion,
        aaid || undefined,
        mpid
      );
      setDTXInit(true);
    });
  }, [dtxScriptLoaded, appBundle, appName, appVersion, searchParams, getMpid, handleAdImpression, handleAdRequested, handleAdRendered, handleAdNotRendered]);

  useEffect(() => {
    // Wait for the DTX SDK to be initialized (not just script-loaded) before loading the prebid
    // wrapper — otherwise window.DTXWebPrebidSpec may be undefined when pbjs.que fires registerBidAdapter.
    if (adConfigData && !pubwiseLoaded && dtxInit) {
      const pubwisePreScript = adConfigData.adTag?.pubwisePreScript
      const pubwiseScript = adConfigData.adTag?.pubwiseScript
      if ((pubwiseScript || pubwisePreScript) && !isDntEnabled()) {
        tearDownPubwise()
        loadAllAdScripts([pubwiseScript, pubwisePreScript]).then(() => {
          setPubwiseLoaded(true);
        })
      } else {
        setPubwiseLoaded(true)
      }
    }
  }, [adConfigData, pubwiseLoaded, dtxInit]);


  // The ad system is ready when the config is loaded, and any required ad systems are initialized.
  const isReady = useMemo(() => {
    const configLoaded = !isLoading;
    // If DTX is not used, its readiness is not a factor.
    // If DTX is used, we need its scripts loaded and the system initialized.
    const dtxReady = !hasDtx || (dtxScriptLoaded && dtxInit);

    // GAM doesn't have a separate initialization signal in this component, so its readiness
    // is tied to the config being loaded.
    return configLoaded && dtxReady && pubwiseLoaded;
  }, [isLoading, hasDtx, dtxScriptLoaded, dtxInit, pubwiseLoaded]);

  // Memoize the context value to prevent unnecessary re-renders in consumers.
  const contextValue = useMemo(() => ({
    isReady,
    adConfigData: adConfigData || null,
    adSource,
  }), [isReady, adConfigData, adSource]);

  const content = useMemo(() => {
    if (!hasDtx && !hasGam) {
      return children
    }
    let currentContent = children
    if (hasGam) {
      currentContent = (
        <GamAdsInitLayout brandGroupId={brandGroupId} utmSource={utmSource}>
          {currentContent}
        </GamAdsInitLayout>
      );
    }

    if (hasDtx) {
      currentContent = <DTXAdsLayout>{currentContent}</DTXAdsLayout>;
    }
    return currentContent
  }, [hasDtx, hasGam, children, brandGroupId, utmSource]);


  return (
    <AdCounterContext.Provider value={adCounterContextValue}>
      <AdsContext.Provider value={contextValue}>
        {content}
      </AdsContext.Provider>
    </AdCounterContext.Provider>
  );
};
